import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Form, Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';

import { BRANDS_FOR_DROPDOWN, UPDATE_PRODUCT_BRANDS } from 'lib/networking/endpoints';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import usePost from 'lib/hooks/usePost';

const ACTIONS = {
  CHANGE_BRAND: 'change_brand',
  REMOVE_BRAND: 'remove_brand',
};

function ChangeBrandModal({ selectedProductsList, onHide, refetch }) {
  const [action, setAction] = useState(ACTIONS.CHANGE_BRAND); // 'change' or 'remove'
  const [selectedBrand, setSelectedBrand] = useState(null);

  const getProductIds = () => {
    const manufacturerProductIds = selectedProductsList.manufacturerProducts || [];
    const vendorProductIds = selectedProductsList.vendorProducts || [];
    return { manufacturerProductIds, vendorProductIds };
  };

  const { postData: updateProductBrands, loading: updatingBrands } = usePost(
    UPDATE_PRODUCT_BRANDS,
    () => {
      Swal.fire({
        icon: 'success',
        text:
          action === ACTIONS.CHANGE_BRAND
            ? 'Successfully updated brand for selected products'
            : 'Successfully removed brand from selected products',
        title: 'Success',
      });
      onHide();
      refetch();
    },
    error =>
      Swal.fire({
        icon: 'error',
        text: error?.response?.data?.message || 'Something went wrong, please try again.',
        title: 'Error',
      }),
  );

  const handleBrandChange = option => {
    setSelectedBrand(option);
  };

  const handleSubmit = () => {
    const { manufacturerProductIds, vendorProductIds } = getProductIds();
    const totalProducts = manufacturerProductIds.length + vendorProductIds.length;

    if (action === ACTIONS.REMOVE_BRAND) {
      // Confirm before removing brand
      Swal.fire({
        title: 'Confirm Brand Removal',
        text: `Are you sure you want to remove the brand from ${totalProducts} products?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove it',
        cancelButtonText: 'Cancel',
      }).then(result => {
        if (result.isConfirmed) {
          updateProductBrands({
            manufacturer_product_ids: manufacturerProductIds,
            vendor_product_ids: vendorProductIds,
            action: ACTIONS.REMOVE_BRAND,
          });
        }
      });
    } else if (action === ACTIONS.CHANGE_BRAND && selectedBrand) {
      // Confirm before changing brand
      Swal.fire({
        title: 'Confirm Brand Change',
        text:
          `Are you sure you want to change the ` +
          `brand to "${selectedBrand.label}" for ${totalProducts} products?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, change it',
        cancelButtonText: 'Cancel',
      }).then(result => {
        if (result.isConfirmed) {
          updateProductBrands({
            manufacturer_product_ids: manufacturerProductIds,
            vendor_product_ids: vendorProductIds,
            action: ACTIONS.CHANGE_BRAND,
            brand_id: selectedBrand.value,
          });
        }
      });
    }
  };

  // Count total products
  const totalProducts =
    (selectedProductsList.manufacturerProducts?.length || 0) +
    (selectedProductsList.vendorProducts?.length || 0);

  return (
    <Modal animation={false} centered show={true} onHide={onHide} size="md">
      <Modal.Header className="py-2 my-2" closeButton>
        <h5 className="font-weight-bold">Manage Brand for Selected Products</h5>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <p>{totalProducts} products selected</p>
          {selectedProductsList.manufacturerProducts?.length > 0 && (
            <p className="mb-1">
              {selectedProductsList.manufacturerProducts.length} manufacturer products
            </p>
          )}
          {selectedProductsList.vendorProducts?.length > 0 && (
            <p className="mb-1">{selectedProductsList.vendorProducts.length} vendor products</p>
          )}
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Action</Form.Label>
          <Form.Control as="select" value={action} onChange={e => setAction(e.target.value)}>
            <option value={ACTIONS.CHANGE_BRAND}>Change Brand</option>
            <option value={ACTIONS.REMOVE_BRAND}>Remove Brand</option>
          </Form.Control>
        </Form.Group>

        {action === ACTIONS.CHANGE_BRAND && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Brand</Form.Label>
              <SearchableScrollableSelect
                autoFocus={false}
                initOption={selectedBrand}
                disabled={updatingBrands}
                onChange={handleBrandChange}
                fetchUrl={BRANDS_FOR_DROPDOWN}
                optionMapper={brand => ({
                  label: brand.global_manufacturer_name
                    ? `${brand.name} by ${brand.global_manufacturer_name}`
                    : brand.name,
                  value: brand.id,
                })}
                placeholder="Select Brand"
              />
            </Form.Group>
          </>
        )}
        {action === ACTIONS.REMOVE_BRAND && (
          <div className="alert alert-warning">
            <p className="mb-0">
              This will remove the brand association from all selected products.
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={
            updatingBrands ||
            (action === ACTIONS.CHANGE_BRAND && !selectedBrand) ||
            totalProducts === 0
          }
        >
          {action === ACTIONS.CHANGE_BRAND ? 'Update Brand' : 'Remove Brand'}
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

ChangeBrandModal.propTypes = {
  selectedProductsList: PropTypes.shape({
    manufacturerProducts: PropTypes.array,
    vendorProducts: PropTypes.array,
  }),
  onHide: PropTypes.func,
  refetch: PropTypes.func,
};

export default ChangeBrandModal;
